import { ConsentOverlayText } from "./consent-overlay-types";

export const PREFERENCE_CENTER_PLACEHOLDER = "{preferenceCenter}";
export const EXTERNAL_CONTENT_PLACEHOLDER = "{externalContent}";

export const DEFAULT_CONSENT_OVERLAY_TEXT: ConsentOverlayText = {
    title: "Odklikněte cookies a zobrazte si obsah",
    message: `Abychom Vám na Allwyn.cz mohli zobrazovat vložený obsah třetích stran, je potřeba Váš souhlas k umístění cookies a podobných technologií, které souvisí s vloženým obsahem třetích stran, a k následnému zpracování Vašich osobních údajů. Bez udělení souhlasu Vám tento obsah nebude zobrazen. Svůj souhlas můžete vyjádřit na tlačítku „souhlasím a zobrazit“ a kdykoli odvolat v sekci ${PREFERENCE_CENTER_PLACEHOLDER}.`,
    preferenceCenterLinkLabel: "podrobnější nastavení",
    externalContentText: `Video můžete zhlédnout přímo na webové stránce poskytovatele ${EXTERNAL_CONTENT_PLACEHOLDER}.`,
    externalContentLinkLabel: "zde",
    consentButtonText: "Souhlasím a zobrazit",
    preferenceCenterButtonText: "Podrobnější nastavení",
};

export const CONSENT_OVERLAY_CLASSES = {
    block: "consent-overlay",
    content: "consent-overlay__content",
    title: "consent-overlay__title",
    text: "consent-overlay__text",
    actions: "consent-overlay__actions",
    button: "consent-overlay__button",
    link: "consent-overlay__link",
    textWrapper: "consent-overlay__text-wrapper",
    preferenceCenterLink: "consent-overlay-preference-center",
} as const;

export function resolveOverlayText(overrides?: Partial<ConsentOverlayText>): ConsentOverlayText {
    return { ...DEFAULT_CONSENT_OVERLAY_TEXT, ...overrides };
}

const escapeAttribute = (value: string) => value.replace(/"/g, "&quot;");

function buildAnchor(href: string, label: string, className?: string): string {
    const classAttribute = className ? ` class="${className}"` : "";
    return `<a href="${escapeAttribute(
        href
    )}" target="_blank" rel="noopener noreferrer"${classAttribute}><strong>${label}</strong></a>`;
}

function injectLink(template: string, placeholder: string, href: string, label: string, className?: string): string {
    return template.replace(placeholder, buildAnchor(href, label, className));
}

interface OverlayMarkupParams {
    text: ConsentOverlayText;
    backgroundImage: string;
    backgroundImageSet?: string;
    preferenceCenterUrl: string;
    externalContentUrl?: string;
}

function buildMessage(text: ConsentOverlayText, preferenceCenterUrl: string, externalContentUrl?: string): string {
    const message = injectLink(
        text.message,
        PREFERENCE_CENTER_PLACEHOLDER,
        preferenceCenterUrl,
        text.preferenceCenterLinkLabel,
        CONSENT_OVERLAY_CLASSES.preferenceCenterLink
    );

    if (!externalContentUrl) {
        return message;
    }

    const externalContent = injectLink(
        text.externalContentText,
        EXTERNAL_CONTENT_PLACEHOLDER,
        externalContentUrl,
        text.externalContentLinkLabel
    );

    return `${message} ${externalContent}`;
}

export function buildConsentOverlayMarkup({
    text,
    backgroundImage,
    backgroundImageSet,
    preferenceCenterUrl,
    externalContentUrl,
}: OverlayMarkupParams): string {
    const { block, content, title, text: textClass, textWrapper, actions, button, link } = CONSENT_OVERLAY_CLASSES;
    const backgroundStyle = backgroundImageSet
        ? escapeAttribute(backgroundImageSet)
        : `url('${escapeAttribute(backgroundImage)}')`;

    return `
        <div class="${block}" style="background-image: ${backgroundStyle}">
            <div class="${content}">
                <div class="${textWrapper}">
                    <div class="${title}">${text.title}</div>
                    <div class="${textClass}">
                        ${buildMessage(text, preferenceCenterUrl, externalContentUrl)}
                    </div>
                </div>
                <div class="${actions}">
                    <button type="button" class="${button} button button--secondary">${text.consentButtonText}</button>
                    <a href="${escapeAttribute(preferenceCenterUrl)}" target="_blank" class="${link}">
                        ${text.preferenceCenterButtonText}
                    </a>
                </div>
            </div>
        </div>
    `;
}
